import React, { useCallback, useEffect, useState } from "react";
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormControlLabel,
	MenuItem,
	Stack,
	Step,
	StepContent,
	StepLabel,
	Stepper,
	Switch,
	TextField,
	Typography,
} from "@mui/material";
import { useEinvoicing } from "services/hooks/einvoicing";

const STATUS_COLORS = {
	not_started: "default",
	keys_generated: "info",
	awaiting_device: "warning",
	verified: "info",
	products_registered: "info",
	active: "success",
	error: "error",
};

const activeStepFor = (config) => {
	if (!config?.has_private_key) return 0;
	if (!config?.public_key_downloaded_at && !config?.device_no) return 1;
	if (!config?.device_no || !config?.tin) return 2;
	return 3;
};

/**
 * EFRIS onboarding for one landlord: RentBeta generates the key pair, the landlord
 * uploads the public key on their EFRIS portal and returns a device number, then
 * staff verify the connection, which registers the rent service and activates receipts.
 */
export default function EfrisOnboardingDialog({ open, landlord, onClose, onChanged }) {
	const {
		isSubmitting,
		getOnboarding,
		generateKeys,
		saveDevice,
		verifyConnection,
		registerProducts,
		saveSettings,
		downloadPublicKey,
		downloadInstructions,
	} = useEinvoicing();
	const [config, setConfig] = useState(null);
	const [loading, setLoading] = useState(false);
	const [feedback, setFeedback] = useState(null);
	const [device, setDevice] = useState({ tin: "", device_no: "", environment: "sandbox" });
	const [verifySteps, setVerifySteps] = useState([]);

	const landlordId = landlord?.id;

	const applyConfig = useCallback((data) => {
		setConfig(data);
		setDevice({
			tin: data?.tin || "",
			device_no: data?.device_no || "",
			environment: data?.environment || "sandbox",
		});
	}, []);

	const load = useCallback(async () => {
		if (!landlordId) return;
		setLoading(true);
		const result = await getOnboarding(landlordId);
		setLoading(false);
		if (result.success) applyConfig(result.data.data);
		else setFeedback({ severity: "error", message: result.message });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [landlordId]);

	useEffect(() => {
		if (open) {
			setFeedback(null);
			setVerifySteps([]);
			load();
		}
	}, [open, load]);

	const handleResult = (result, successMessage) => {
		if (result.data?.data) applyConfig(result.data.data);
		if (result.success) {
			setFeedback({ severity: "success", message: successMessage });
			onChanged?.();
		} else {
			setFeedback({ severity: "error", message: result.message });
		}
		return result;
	};

	const handleGenerateKeys = async () => {
		let force = false;
		if (config?.has_private_key) {
			const ok = window.confirm(
				"This landlord already has keys. Regenerating replaces the key uploaded on " +
					"their EFRIS portal and they will have to upload the new public key. Continue?"
			);
			if (!ok) return;
			force = true;
		}
		handleResult(await generateKeys(landlordId, force), "Keys generated. Download the public key for the landlord.");
	};

	const handleDownloadKey = async (type) => {
		const result = await downloadPublicKey(landlordId, type);
		if (!result.success) setFeedback({ severity: "error", message: result.message });
		else load();
	};

	const handleDownloadInstructions = async () => {
		const result = await downloadInstructions(landlordId);
		if (!result.success) setFeedback({ severity: "error", message: result.message });
	};

	const handleSaveDevice = async () => {
		handleResult(await saveDevice(landlordId, device), "Device details saved. Now verify the connection.");
	};

	const handleVerify = async () => {
		const result = await verifyConnection(landlordId);
		setVerifySteps(result.data?.result?.steps || []);
		handleResult(
			result,
			"Connection verified, rent service registered and EFRIS receipts activated."
		);
	};

	const handleRegisterProducts = async () => {
		handleResult(await registerProducts(landlordId), "Rent service registered on EFRIS.");
	};

	const handleVatChange = async (event) => {
		const vat_registered = event.target.checked;
		const result = await saveSettings(landlordId, { vat_registered });
		handleResult(
			result,
			vat_registered
				? "Set to VAT-registered (standard 18%). Re-register products to update EFRIS."
				: "Set to non-VAT (exempt e-receipts). Re-register products to update EFRIS."
		);
	};

	const seller = config?.seller_details;
	const isVerified = Boolean(config?.verified_at);

	return (
		<Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
			<DialogTitle>
				<Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
					<Box>
						<Typography variant="h4">EFRIS onboarding</Typography>
						<Typography variant="body2" color="text.secondary">
							{landlord?.business_name}
						</Typography>
					</Box>
					{config && (
						<Chip
							label={config.onboarding_status_label}
							color={STATUS_COLORS[config.onboarding_status] || "default"}
						/>
					)}
				</Stack>
			</DialogTitle>
			<DialogContent dividers>
				{loading && !config ? (
					<Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
						<CircularProgress />
					</Box>
				) : (
					<Stack spacing={2}>
						{feedback && (
							<Alert severity={feedback.severity} onClose={() => setFeedback(null)}>
								{feedback.message}
							</Alert>
						)}
						{config?.onboarding_status === "error" && config?.onboarding_error && (
							<Alert severity="error">
								<strong>Last error ({config.onboarding_error_step || "onboarding"}):</strong>{" "}
								{config.onboarding_error}
							</Alert>
						)}

						<Stepper activeStep={activeStepFor(config)} orientation="vertical" nonLinear>
							<Step completed={Boolean(config?.has_private_key)} expanded>
								<StepLabel>Generate the landlord's EFRIS keys</StepLabel>
								<StepContent>
									<Typography variant="body2" sx={{ mb: 1 }}>
										RentBeta creates the key pair. The private key stays encrypted on RentBeta's
										server; only the public key is shared with the landlord.
									</Typography>
									<Button variant="outlined" disabled={isSubmitting} onClick={handleGenerateKeys}>
										{config?.has_private_key ? "Regenerate keys" : "Generate keys"}
									</Button>
								</StepContent>
							</Step>

							<Step completed={Boolean(config?.public_key_downloaded_at)} expanded>
								<StepLabel>Landlord connects RentBeta on the EFRIS portal</StepLabel>
								<StepContent>
									<Typography variant="body2" sx={{ mb: 1 }}>
										Send the landlord the public key and the printable instructions. They log in
										to EFRIS with their own credentials, upload the public key for
										System-to-System integration and send back the device number.
									</Typography>
									<Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
										<Button
											variant="outlined"
											disabled={!config?.has_certificate || isSubmitting}
											onClick={() => handleDownloadKey("crt")}
										>
											Public key (.crt)
										</Button>
										<Button
											disabled={!config?.has_certificate || isSubmitting}
											onClick={() => handleDownloadKey("cer")}
										>
											.cer
										</Button>
										<Button
											disabled={!config?.has_certificate || isSubmitting}
											onClick={() => handleDownloadKey("pem")}
										>
											.pem
										</Button>
										<Button variant="outlined" disabled={isSubmitting} onClick={handleDownloadInstructions}>
											Instructions (PDF)
										</Button>
									</Stack>
								</StepContent>
							</Step>

							<Step completed={Boolean(config?.device_no && config?.tin)} expanded>
								<StepLabel>Enter the landlord's TIN and device number</StepLabel>
								<StepContent>
									<Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 1 }}>
										<TextField
											size="small"
											label="TIN"
											value={device.tin}
											onChange={(e) => setDevice({ ...device, tin: e.target.value.trim() })}
										/>
										<TextField
											size="small"
											label="Device number"
											value={device.device_no}
											onChange={(e) => setDevice({ ...device, device_no: e.target.value.trim() })}
										/>
										<TextField
											size="small"
											select
											label="Environment"
											value={device.environment}
											onChange={(e) => setDevice({ ...device, environment: e.target.value })}
											sx={{ minWidth: 140 }}
										>
											<MenuItem value="sandbox">Sandbox</MenuItem>
											<MenuItem value="production">Production</MenuItem>
										</TextField>
										<Button
											variant="outlined"
											disabled={!config?.has_private_key || !device.device_no || !device.tin || isSubmitting}
											onClick={handleSaveDevice}
										>
											Save
										</Button>
									</Stack>
								</StepContent>
							</Step>

							<Step completed={config?.onboarding_status === "active"} expanded>
								<StepLabel>Verify connection and activate</StepLabel>
								<StepContent>
									<Typography variant="body2" sx={{ mb: 1 }}>
										Runs the EFRIS handshake with the landlord's credentials, saves the seller
										details URA holds for this TIN, registers the rent service and activates
										EFRIS receipts.
									</Typography>
									<Stack direction="row" spacing={1}>
										<Button
											variant="contained"
											disabled={!config?.device_no || !config?.has_private_key || isSubmitting}
											onClick={handleVerify}
										>
											{isSubmitting ? "Working…" : "Verify connection"}
										</Button>
										{isVerified && (
											<Button disabled={isSubmitting} onClick={handleRegisterProducts}>
												Re-register products
											</Button>
										)}
									</Stack>
									{verifySteps.length > 0 && (
										<Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
											{verifySteps.map((step) => (
												<Chip
													key={step.code}
													size="small"
													label={`${step.code} ${step.ok ? "OK" : step.non_fatal ? "skipped" : "failed"}`}
													color={step.ok ? "success" : step.non_fatal ? "default" : "error"}
												/>
											))}
										</Stack>
									)}
								</StepContent>
							</Step>
						</Stepper>

						<Divider />
						<FormControlLabel
							control={
								<Switch
									checked={Boolean(config?.vat_registered)}
									disabled={!config || !isVerified || isSubmitting}
									onChange={handleVatChange}
								/>
							}
							label={
								config?.vat_registered
									? "VAT-registered: e-invoices with standard 18% VAT on rent"
									: "Not VAT-registered: VAT-exempt e-receipts"
							}
						/>
						{!isVerified && (
							<Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
								VAT status is read from URA when the connection is verified. You can change it
								afterwards.
							</Typography>
						)}

						{isVerified && seller && (
							<Box>
								<Typography variant="h6" sx={{ mb: 1 }}>
									Seller details on receipts (from URA)
								</Typography>
								<Typography variant="body2">
									<strong>{seller.legalName}</strong>
									{seller.businessName && seller.businessName !== seller.legalName
										? ` (${seller.businessName})`
										: ""}
								</Typography>
								<Typography variant="body2">TIN {seller.tin}{seller.ninBrn ? ` · NIN/BRN ${seller.ninBrn}` : ""}</Typography>
								{seller.address && <Typography variant="body2">{seller.address}</Typography>}
								<Typography variant="body2">
									{[seller.mobilePhone, seller.emailAddress].filter(Boolean).join(" · ")}
								</Typography>
							</Box>
						)}
					</Stack>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Close</Button>
			</DialogActions>
		</Dialog>
	);
}
